import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { fmtDate, VAT_RATES, vatIncl, sessionHours, fmtHours } from "@/lib/format";
import { LogOut, Clock, LogIn as LogInIcon, LogOut as ClockOutIcon, Save, Loader2, Plus, Trash2, Truck, Users as UsersIcon, Camera, ClipboardList, Lock, ChevronLeft, MapPin } from "lucide-react";
import { toast } from "sonner";
import { InstallApp } from "@/components/InstallApp";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—");

const ORARI_FIELDS = [
  ["partenza_magazzino", "Partenza magazzino"], ["arrivo_location", "Arrivo location"], ["inizio_evento", "Inizio evento"],
  ["fine_evento", "Fine evento"], ["fine_smontaggio", "Fine smontaggio"], ["rientro", "Rientro"],
];
const TURNI_OPTS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4];
const emptyReport = () => ({ event_id: "", title: "", event_date: "", vehicles: [], danni_veicolo: "", shifts: [], extratime: 0, altri_personale: "", materiale_extra: "", orari: {}, spese: [], foto_spese: [], materiale_guasto: "", foto_guasto: [], problemi_cliente: "", problemi_personale: "" });
const toBase64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

function SessionCard({ s, idx }) {
  const [mandatino, setMandatino] = useState(s.mandatino || "");
  const [pausa, setPausa] = useState(s.pausa_min || 0);
  const [sede, setSede] = useState(s.sede || "");
  const [busy, setBusy] = useState(false);
  const open = !s.clock_out;
  const save = async () => {
    setBusy(true);
    try { await api.saveMandatino({ id: s.id, mandatino, pausa_min: Number(pausa) || 0, sede }); toast.success("Mandatino salvato"); }
    catch { toast.error("Errore"); }
    setBusy(false);
  };
  return (
    <div data-testid={`session-${s.id}`} className="bg-[#1A2335] rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-300 flex items-center gap-2">
          Sessione {idx + 1}
          {open && <span className="text-[9px] uppercase bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded">In corso</span>}
        </span>
        <span className="font-mono text-sm">
          <span data-testid={`session-in-${s.id}`} className="text-emerald-400">{hhmm(s.clock_in)}</span>
          <span className="text-slate-600"> → </span>
          <span data-testid={`session-out-${s.id}`} className="text-sky-400">{hhmm(s.clock_out)}</span>
        </span>
      </div>
      {(s.lat_in || s.lat_out) && <p className="text-[10px] text-slate-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> Posizione registrata</p>}
      {!open && <p data-testid={`session-hours-${s.id}`} className="text-[11px] font-mono text-amber-400">Ore lavorate: {fmtHours(sessionHours(s))}{s.pausa_min > 0 ? ` (pausa ${s.pausa_min}m esclusa)` : ""}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div><label className="lbl">Sede / Cantiere</label><input data-testid={`session-sede-${s.id}`} value={sede} onChange={(e) => setSede(e.target.value)} className="inp" placeholder="Es. Villa Rosa…" /></div>
        <div><label className="lbl">Pausa (min)</label><input data-testid={`session-pausa-${s.id}`} type="number" value={pausa} onChange={(e) => setPausa(e.target.value)} className="inp font-mono" /></div>
      </div>
      <div>
        <label className="lbl">Attività svolte</label>
        <textarea data-testid={`session-text-${s.id}`} value={mandatino} onChange={(e) => setMandatino(e.target.value)} rows={2} className="inp resize-none" placeholder="Descrivi il lavoro di questa sessione…" />
      </div>
      <button data-testid={`session-save-${s.id}`} disabled={busy} onClick={save} className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-semibold py-2 rounded-lg transition-colors text-sm">
        <Save className="w-4 h-4" /> Salva mandatino
      </button>
    </div>
  );
}

function Timbratura() {
  const [sessions, setSessions] = useState(undefined); // undefined = loading
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => api.timbraturaToday().then((d) => setSessions(d || [])).catch(() => setSessions([]));
  const loadHistory = () => api.listTimbrature().then((d) => setHistory((d || []).slice(0, 10))).catch(() => {});
  const getGeo = () => new Promise((res) => {
    if (!navigator.geolocation) return res({});
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => res({}), { timeout: 5000, enableHighAccuracy: true }
    );
  });

  useEffect(() => { load(); loadHistory(); }, []);

  const doClockIn = async () => { setBusy(true); try { const geo = await getGeo(); await api.clockIn(geo); await load(); loadHistory(); toast.success("Entrata registrata"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } setBusy(false); };
  const doClockOut = async () => { setBusy(true); try { const geo = await getGeo(); await api.clockOut(geo); await load(); loadHistory(); toast.success("Uscita registrata"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } setBusy(false); };

  if (sessions === undefined) return <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const hasOpen = sessions.some((s) => !s.clock_out);

  return (
    <div className="space-y-4">
      <div data-testid="clock-area" className="bg-[#121824] border border-[#222D42] rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0"><Clock className="w-5 h-5" /></div>
          <div>
            <h3 className="font-head font-semibold text-slate-100">Timbrature di oggi</h3>
            <p className="text-slate-500 text-xs">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} · {sessions.length} session{sessions.length === 1 ? "e" : "i"}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button data-testid="clock-in-button" disabled={busy || hasOpen} onClick={doClockIn} className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold py-2.5 rounded-lg transition-colors">
            <LogInIcon className="w-4 h-4" /> Timbra entrata
          </button>
          <button data-testid="clock-out-button" disabled={busy || !hasOpen} onClick={doClockOut} className="flex-1 flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold py-2.5 rounded-lg transition-colors">
            <ClockOutIcon className="w-4 h-4" /> Timbra uscita
          </button>
        </div>

        {sessions.length === 0 ? (
          <p className="text-slate-600 text-sm text-center py-2">Nessuna sessione registrata oggi. Timbra l'entrata per iniziare.</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((s, idx) => <SessionCard key={s.id} s={s} idx={idx} />)}
          </div>
        )}
      </div>

      <div data-testid="timbrature-history" className="bg-[#121824] border border-[#222D42] rounded-xl p-5">
        <h3 className="font-head font-semibold text-slate-100 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Le mie ultime timbrature</h3>
        {history.length === 0 ? <p className="text-slate-600 text-sm">Nessuna timbratura registrata.</p> : (
          <div className="divide-y divide-[#222D42]">
            {history.map((h) => (
              <div key={h.id} data-testid={`timbratura-hist-${h.id}`} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-300">{new Date(h.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" })}</span>
                <span className="font-mono text-xs"><span className="text-emerald-400">{hhmm(h.clock_in)}</span> <span className="text-slate-600">→</span> <span className="text-sky-400">{hhmm(h.clock_out)}</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`.inp{width:100%;background:#1A2335;border:1px solid #222D42;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#F1F5F9}.inp:focus{outline:none;border-color:rgba(229,169,60,.5)}.lbl{font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;color:#64748B;font-weight:500;display:block;margin-bottom:.375rem}`}</style>
    </div>
  );
}

function TechReports() {
  const [events, setEvents] = useState(undefined);
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(emptyReport());
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newEv, setNewEv] = useState({ name: "", client: "", date: "", location: "", type: "matrimonio" });

  const loadEvents = () => api.techEvents().then(setEvents).catch(() => setEvents([]));
  useEffect(() => {
    loadEvents();
    Promise.all([api.techStaff(), api.techVehicles(), api.listExpenseCategories(), api.listEventTypes()])
      .then(([s, v, c, t]) => { setStaff(s || []); setVehicles(v || []); setCategories(c || []); setEventTypes(t || []); }).catch(() => {});
  }, []);

  const createEvent = async () => {
    if (!newEv.name.trim()) return toast.error("Inserisci il nome dell'evento");
    try {
      const ev = await api.techCreateEvent({ ...newEv, name: newEv.name.trim() });
      toast.success("Evento creato");
      setShowNew(false); setNewEv({ name: "", client: "", date: "", location: "", type: "matrimonio" });
      openEvent(ev);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella creazione evento"); }
  };

  const openEvent = async (ev) => {
    setSel(ev); setLocked(!!ev.chiuso);
    const r = await api.techGetReport(ev.id).catch(() => null);
    if (r) { let spese = r.spese || []; setForm({ ...emptyReport(), ...r, spese, orari: r.orari || {} }); }
    else setForm({ ...emptyReport(), event_id: ev.id, title: ev.name, event_date: ev.date });
  };
  const back = () => { setSel(null); setForm(emptyReport()); loadEvents(); };

  const addRow = (k, row) => setForm((f) => ({ ...f, [k]: [...f[k], row] }));
  const updRow = (k, i, patch) => setForm((f) => { const a = [...f[k]]; a[i] = { ...a[i], ...patch }; return { ...f, [k]: a }; });
  const rmRow = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, idx) => idx !== i) }));
  const setOrario = (k, val) => setForm((f) => ({ ...f, orari: { ...f.orari, [k]: val } }));
  const onPhotos = async (e, k) => { const files = [...e.target.files]; const b = await Promise.all(files.map(toBase64)); setForm((f) => ({ ...f, [k]: [...(f[k] || []), ...b] })); };

  const save = async () => {
    setBusy(true);
    const payload = { ...form, extratime: Number(form.extratime) || 0,
      spese: (form.spese || []).map((x) => ({ id: x.id, categoria: x.categoria || "", amount: Number(x.amount) || 0, vat_rate: Number(x.vat_rate) || 0, fattura_elettronica: !!x.fattura_elettronica, rimborso_tecnico_id: x.rimborso_tecnico_id || null, rimborso_tecnico_nome: x.rimborso_tecnico_nome || "", note: x.note || "" })),
      spese_totale: (form.spese || []).reduce((s, x) => s + (Number(x.amount) || 0), 0),
      vehicles: form.vehicles.map((v) => ({ ...v, km: Number(v.km) || 0 })),
      shifts: form.shifts.map((s) => ({ ...s, turni: Number(s.turni) || 0 })) };
    try { await api.techSaveReport(payload); toast.success("Report salvato"); back(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    setBusy(false);
  };

  if (events === undefined) return <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  if (!sel) return (
    <div data-testid="tech-event-list" className="space-y-3">
      <h3 className="font-head font-semibold text-slate-100 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-400" /> Scegli l'evento da compilare</h3>
      {events.length === 0 && <p className="text-slate-500 text-sm">Nessun evento disponibile.</p>}
      {events.map((e) => (
        <button key={e.id} data-testid={`tech-event-${e.id}`} onClick={() => openEvent(e)} className="w-full text-left bg-[#121824] border border-[#222D42] hover:border-[#384B6E] rounded-xl p-4 transition-colors flex items-center justify-between gap-3">
          <div className="min-w-0"><p className="text-slate-100 text-sm font-medium truncate">{e.name}</p><p className="text-slate-500 text-xs">{e.client || "—"} · {fmtDate(e.date)}{e.location ? ` · ${e.location}` : ""}</p></div>
          <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full shrink-0 ${e.chiuso ? "bg-slate-500/10 text-slate-400 border border-slate-500/20" : e.report_id ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>{e.chiuso ? "chiuso" : e.report_id ? "compilato" : "da fare"}</span>
        </button>
      ))}

      {showNew ? (
        <div data-testid="tech-new-event-form" className="bg-[#121824] border border-amber-500/30 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-amber-400">Nuovo evento</p>
          <div><label className="lbl">Nome evento *</label><input data-testid="tech-newevent-name" value={newEv.name} onChange={(e) => setNewEv({ ...newEv, name: e.target.value })} className="inp" placeholder="Es. Matrimonio Rossi" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Cliente</label><input data-testid="tech-newevent-client" value={newEv.client} onChange={(e) => setNewEv({ ...newEv, client: e.target.value })} className="inp" /></div>
            <div><label className="lbl">Data</label><input type="date" data-testid="tech-newevent-date" value={newEv.date} onChange={(e) => setNewEv({ ...newEv, date: e.target.value })} className="inp" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="lbl">Luogo</label><input data-testid="tech-newevent-location" value={newEv.location} onChange={(e) => setNewEv({ ...newEv, location: e.target.value })} className="inp" /></div>
            <div><label className="lbl">Tipo</label><select data-testid="tech-newevent-type" value={newEv.type} onChange={(e) => setNewEv({ ...newEv, type: e.target.value })} className="inp">{eventTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
          </div>
          <div className="flex gap-2">
            <button data-testid="tech-create-event" onClick={createEvent} className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2 rounded-lg transition-colors">Crea e compila report</button>
            <button onClick={() => setShowNew(false)} className="bg-[#1A2335] border border-[#222D42] text-slate-300 px-4 py-2 rounded-lg">Annulla</button>
          </div>
          <style>{`.inp{width:100%;background:#1A2335;border:1px solid #222D42;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#F1F5F9}.inp:focus{outline:none;border-color:rgba(229,169,60,.5)}.lbl{font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;color:#64748B;font-weight:500;display:block;margin-bottom:.375rem}`}</style>
        </div>
      ) : (
        <button data-testid="tech-add-event" onClick={() => setShowNew(true)} className="w-full border border-dashed border-[#384B6E] rounded-xl p-4 text-slate-400 hover:text-amber-400 hover:border-amber-500/50 transition-colors flex items-center justify-center gap-2 text-sm"><Plus className="w-4 h-4" /> Evento non in lista? Crea nuovo evento</button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button data-testid="tech-back" onClick={back} className="text-slate-400 hover:text-amber-400 text-sm flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Indietro</button>
        {locked && <span className="text-xs text-slate-400 flex items-center gap-1 bg-slate-500/10 border border-slate-500/20 rounded-full px-2 py-1"><Lock className="w-3.5 h-3.5" /> Chiuso dall'admin</span>}
      </div>
      <div className="bg-[#121824] border border-[#222D42] rounded-xl p-4"><p className="text-slate-100 font-medium">{sel.name}</p><p className="text-slate-500 text-xs">{sel.client || "—"} · {fmtDate(sel.date)}</p></div>

      <fieldset disabled={locked} className={locked ? "opacity-60 pointer-events-none space-y-4" : "space-y-4"}>
        <Block icon={UsersIcon} title="Personale su evento (turni)">
          {form.shifts.map((sh, i) => (
            <div key={i} className="flex gap-2 items-center flex-wrap">
              <select data-testid="tech-shift-staff" value={sh.staff_id || ""} onChange={(e) => updRow("shifts", i, { staff_id: e.target.value, name: staff.find((x) => x.id === e.target.value)?.name || "" })} className="inp flex-1 min-w-[160px]"><option value="">— Tecnico —</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
              <select value={sh.turni} onChange={(e) => updRow("shifts", i, { turni: e.target.value })} className="inp w-20">{TURNI_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <button onClick={() => rmRow("shifts", i)} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" data-testid="tech-add-shift" onClick={() => addRow("shifts", { staff_id: "", name: "", turni: 1 })} className="text-amber-400 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Aggiungi tecnico</button>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div><label className="lbl">Extratime (turni)</label><input type="number" step="0.5" value={form.extratime} onChange={(e) => setForm({ ...form, extratime: e.target.value })} className="inp font-mono" /></div>
            <div><label className="lbl">Altri non in lista</label><input value={form.altri_personale} onChange={(e) => setForm({ ...form, altri_personale: e.target.value })} className="inp" /></div>
          </div>
        </Block>

        <Block icon={Truck} title="Veicoli usati">
          {form.vehicles.map((rv, i) => (
            <div key={i} className="flex gap-2 items-center flex-wrap">
              <select value={rv.vehicle_id || ""} onChange={(e) => updRow("vehicles", i, { vehicle_id: e.target.value, label: e.target.value === "__altro__" ? (rv.label || "") : "" })} className="inp flex-1 min-w-[140px]"><option value="">— Veicolo —</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} {v.plate}</option>)}<option value="__altro__">Altro / a noleggio…</option></select>
              {rv.vehicle_id === "__altro__" && <input value={rv.label || ""} onChange={(e) => updRow("vehicles", i, { label: e.target.value })} placeholder="Nome veicolo" className="inp flex-1 min-w-[120px]" />}
              <input type="number" value={rv.km} onChange={(e) => updRow("vehicles", i, { km: e.target.value })} placeholder="km" className="inp w-20 font-mono" />
              <button onClick={() => rmRow("vehicles", i)} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" data-testid="tech-add-vehicle" onClick={() => addRow("vehicles", { vehicle_id: "", km: 0 })} className="text-amber-400 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Aggiungi veicolo</button>
          <div className="pt-1">
            <div><label className="lbl">Danni / problemi veicolo</label><input value={form.danni_veicolo} onChange={(e) => setForm({ ...form, danni_veicolo: e.target.value })} className="inp" /></div>
          </div>
        </Block>

        <Block icon={Clock} title="Orari">
          <div className="grid grid-cols-2 gap-3">
            {ORARI_FIELDS.map(([k, lbl]) => (<div key={k}><label className="lbl">{lbl}</label><input type="time" value={form.orari[k] || ""} onChange={(e) => setOrario(k, e.target.value)} className="inp" /></div>))}
          </div>
        </Block>

        <Block icon={Camera} title="Spese, rimborsi e segnalazioni">
          <div className="space-y-2">
            {form.spese.map((sp, i) => (
              <div key={i} data-testid={`tech-spesa-${i}`} className="bg-[#1A2335] border border-[#222D42] rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <select value={sp.categoria || ""} onChange={(e) => updRow("spese", i, { categoria: e.target.value })} className="inp"><option value="">— Categoria —</option>{categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
                  <input type="number" value={sp.amount} onChange={(e) => updRow("spese", i, { amount: e.target.value })} className="inp font-mono" placeholder="Importo IVA incl. (€)" />
                </div>
                <div className="grid grid-cols-2 gap-2 items-center">
                  <select value={sp.vat_rate} onChange={(e) => updRow("spese", i, { vat_rate: Number(e.target.value) })} className="inp">{VAT_RATES.map((t) => <option key={t.value} value={t.value}>IVA {t.label}</option>)}</select>
                  <span className="text-xs text-slate-500 font-mono">IVA {fmtEurLocal(vatIncl(sp.amount, sp.vat_rate))}</span>
                </div>
                <input value={sp.note || ""} onChange={(e) => updRow("spese", i, { note: e.target.value })} className="inp" placeholder="Note (es. pranzo, carburante…)" />
                <div><label className="lbl">Rimborso a tecnico (se spesa anticipata)</label><select data-testid={`tech-spesa-rimborso-${i}`} value={sp.rimborso_tecnico_id || ""} onChange={(e) => updRow("spese", i, { rimborso_tecnico_id: e.target.value || null, rimborso_tecnico_nome: staff.find((x) => x.id === e.target.value)?.name || "" })} className="inp"><option value="">— Nessun rimborso —</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer"><input type="checkbox" checked={!!sp.fattura_elettronica} onChange={(e) => updRow("spese", i, { fattura_elettronica: e.target.checked })} className="accent-amber-500 w-4 h-4" /> Fattura elettronica</label>
                  <button onClick={() => rmRow("spese", i)} className="text-red-400 p-1 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Rimuovi</button>
                </div>
              </div>
            ))}
            <button data-testid="tech-add-spesa" onClick={() => addRow("spese", { categoria: "", amount: 0, vat_rate: 0, fattura_elettronica: false, note: "" })} className="text-sm text-amber-400 flex items-center gap-1"><Plus className="w-4 h-4" /> Aggiungi spesa</button>
          </div>
          <PhotoRow label="Foto scontrini" photos={form.foto_spese} onAdd={(e) => onPhotos(e, "foto_spese")} onRemove={(idx) => rmRow("foto_spese", idx)} />
          <div><label className="lbl">Materiale extra last minute</label><input value={form.materiale_extra} onChange={(e) => setForm({ ...form, materiale_extra: e.target.value })} className="inp" /></div>
          <div><label className="lbl">Materiale guasto da segnalare</label><input value={form.materiale_guasto} onChange={(e) => setForm({ ...form, materiale_guasto: e.target.value })} className="inp" /></div>
          <PhotoRow label="Foto materiale guasto" photos={form.foto_guasto} onAdd={(e) => onPhotos(e, "foto_guasto")} onRemove={(idx) => rmRow("foto_guasto", idx)} />
          <div><label className="lbl">Problemi con cliente / location</label><textarea value={form.problemi_cliente} onChange={(e) => setForm({ ...form, problemi_cliente: e.target.value })} className="inp min-h-[50px]" /></div>
          <div><label className="lbl">Problemi comportamentali personale</label><textarea value={form.problemi_personale} onChange={(e) => setForm({ ...form, problemi_personale: e.target.value })} className="inp min-h-[50px]" /></div>
        </Block>
      </fieldset>

      {!locked && (
        <button data-testid="tech-save-report" disabled={busy} onClick={save} className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-semibold py-2.5 rounded-lg transition-colors">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva report</button>
      )}

      <style>{`.inp{width:100%;background:#1A2335;border:1px solid #222D42;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#F1F5F9}.inp:focus{outline:none;border-color:rgba(229,169,60,.5)}.lbl{font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;color:#64748B;font-weight:500;display:block;margin-bottom:.375rem}`}</style>
    </div>
  );
}

const fmtEurLocal = (n) => "€" + (Number(n) || 0).toFixed(2);

const Block = ({ icon: Icon, title, children }) => (
  <div className="border border-[#222D42] rounded-xl p-4 space-y-3 bg-[#121824]">
    <h4 className="font-head text-sm font-semibold text-slate-200 flex items-center gap-2"><Icon className="w-4 h-4 text-amber-400" /> {title}</h4>
    {children}
  </div>
);

const PhotoRow = ({ label, photos, onAdd, onRemove }) => (
  <div>
    <label className="lbl">{label}</label>
    <div className="flex flex-wrap gap-2 items-center">
      {(photos || []).map((p, i) => (
        <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#222D42]">
          <img src={p} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onRemove(i)} className="absolute top-0 right-0 bg-black/70 text-red-400 p-0.5 rounded-bl"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
      <label className="w-16 h-16 rounded-lg border border-dashed border-[#384B6E] flex items-center justify-center cursor-pointer text-slate-500 hover:text-amber-400 hover:border-amber-500/50 transition-colors">
        <Camera className="w-5 h-5" />
        <input type="file" accept="image/*" multiple capture="environment" onChange={onAdd} className="hidden" />
      </label>
    </div>
  </div>
);

export default function StaffHome() {
  const { user, logout } = useAuth();
  const isTech = user?.role === "tecnico";

  return (
    <div className="min-h-screen bg-[#090D14] text-slate-200">
      <header className="flex items-center justify-between px-5 py-4 border-b border-[#222D42]">
        <div className="flex items-center gap-3">
          <img src="/betti-logo.jpg" alt="Betti SRL" className="w-10 h-10 rounded-lg object-cover bg-white" />
          <span className="font-head font-bold text-slate-100">Betti SRL</span>
        </div>
        <button data-testid="logout-button" onClick={logout} className="flex items-center gap-2 text-slate-400 hover:text-red-400 text-sm px-3 py-1.5 rounded-lg hover:bg-red-500/10 transition-colors">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-10 animate-fade-up">
        <InstallApp />
        <h1 className="font-head text-3xl font-bold text-slate-100">Ciao {user?.name || ""}</h1>
        <p className="text-slate-500 mt-1 text-sm">Area {isTech ? "Tecnico" : "Operativa"} · Betti SRL</p>

        <div className="mt-8 space-y-8">
          <Timbratura />
          {isTech && <TechReports />}
        </div>
      </main>
    </div>
  );
}
